import { animate } from "motion/mini";
import type { DOMKeyframesDefinition, AnimationOptions } from "motion";
import type { Easing } from "motion-utils";
import { tokens } from "@data/design-tokens";
import { prefersReducedMotion } from "./reduced-motion";
import type {
  AnimationController,
  BloomBurstOptions,
  FadeOptions,
  SceneTransitionOptions,
} from "./animation-controller";

// cubic-bezier strings не входят в тип Easing motion, но браузер их понимает.
// Кастируем безопасно через unknown.
const easingStandard = tokens.easing.standard as unknown as Easing;
const easingEmphasized = tokens.easing.emphasized as unknown as Easing;

/** AnimationController на Motion One. Возвращает мгновенно завершённые промисы при prefers-reduced-motion. */
export class MotionAnimationController implements AnimationController {
  async fadeIn(el: HTMLElement, opts: FadeOptions = {}): Promise<void> {
    if (prefersReducedMotion()) {
      el.style.opacity = "1";
      return;
    }
    const duration = (opts.duration ?? tokens.motion.normal) / 1000;
    const keyframes = { opacity: [0, 1] } as unknown as DOMKeyframesDefinition;
    const options: AnimationOptions = { duration, ease: easingStandard };
    await animate(el, keyframes, options).finished;
  }

  async fadeOut(el: HTMLElement, opts: FadeOptions = {}): Promise<void> {
    if (prefersReducedMotion()) {
      el.style.opacity = "0";
      return;
    }
    const duration = (opts.duration ?? tokens.motion.normal) / 1000;
    const keyframes = { opacity: [1, 0] } as unknown as DOMKeyframesDefinition;
    const options: AnimationOptions = { duration, ease: easingStandard };
    await animate(el, keyframes, options).finished;
  }

  async popCollect(el: HTMLElement): Promise<void> {
    if (prefersReducedMotion()) {
      el.style.opacity = "0";
      return;
    }
    const keyframes = {
      transform: ["scale(1)", "scale(1.4)", "scale(0.7)"],
      opacity: [1, 1, 0],
    } as unknown as DOMKeyframesDefinition;
    const options: AnimationOptions = {
      duration: tokens.motion.normal / 1000,
      ease: easingEmphasized,
    };
    await animate(el, keyframes, options).finished;
  }

  async bloomBurst(els: readonly HTMLElement[], opts: BloomBurstOptions = {}): Promise<void> {
    if (prefersReducedMotion()) {
      els.forEach((el) => {
        el.style.opacity = "1";
        el.style.transform = "scale(1)";
      });
      return;
    }
    const staggerMs = opts.staggerMs ?? 70;
    const keyframes = {
      transform: ["scale(0)", "scale(1.18)", "scale(1)"],
      opacity: [0, 1, 1],
    } as unknown as DOMKeyframesDefinition;
    const promises = els.map(
      (el, index) =>
        new Promise<void>((resolve) => {
          window.setTimeout(() => {
            const options: AnimationOptions = { duration: 0.9, ease: "easeOut" };
            animate(el, keyframes, options).finished.then(
              () => resolve(),
              () => resolve(),
            );
          }, index * staggerMs);
        }),
    );
    await Promise.all(promises);
  }

  async sceneTransition(
    from: HTMLElement | null,
    to: HTMLElement,
    opts: SceneTransitionOptions = {},
  ): Promise<void> {
    const duration = opts.duration ?? tokens.motion.normal;
    const kind = opts.kind ?? "fade";
    if (prefersReducedMotion()) {
      if (from) from.style.opacity = "0";
      to.style.opacity = "1";
      return;
    }
    if (from) await this.fadeOut(from, { duration });
    if (kind === "scale") {
      const keyframes = {
        opacity: [0, 1],
        transform: ["scale(0.96)", "scale(1)"],
      } as unknown as DOMKeyframesDefinition;
      const options: AnimationOptions = {
        duration: duration / 1000,
        ease: easingEmphasized,
      };
      await animate(to, keyframes, options).finished;
    } else {
      await this.fadeIn(to, { duration });
    }
  }

  glowToggle(active: boolean): void {
    // CSS-var управление через document.documentElement
    if (prefersReducedMotion()) {
      document.documentElement.style.setProperty("--glow-intensity", active ? "1" : "0");
      return;
    }
    const keyframes = {
      "--glow-intensity": active ? [0, 1] : [1, 0],
    } as unknown as DOMKeyframesDefinition;
    const options: AnimationOptions = { duration: 0.18, ease: easingStandard };
    void animate(document.documentElement, keyframes, options);
  }
}
